import { useState } from 'react';

import { reasonFrom } from '../api/reason';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router';
import { Button, Field, Input, LINK_CLASS, Notice } from '@efira/ui';
import { fieldErrors, resetPasswordSchema } from '@efira/contracts';

import { authClient } from '../auth/client';
import { AuthLayout } from './AuthLayout';

/**
 * Установка нового пароля по ссылке из письма.
 *
 * Истёкшая и уже использованная ссылка обрабатываются ВНЯТНО: пользователь,
 * который открыл письмо через сутки или нажал ссылку второй раз, видит
 * объяснение и кнопку запросить новую, а не «ошибка».
 */
export function ResetPasswordScreen() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token');
  // Better Auth возвращает сюда с ?error=INVALID_TOKEN, если ссылка негодна.
  const linkError = params.get('error');

  const [password, setPassword] = useState('');
  const [passwordRepeat, setPasswordRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [expired, setExpired] = useState(linkError !== null || token === null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(event: React.FormEvent) {
    event.preventDefault();
    setFormError(null);

    const parsed = resetPasswordSchema.safeParse({ password, passwordRepeat });

    if (!parsed.success) {
      setErrors(fieldErrors(parsed.error));
      return;
    }

    if (token === null) {
      setExpired(true);
      return;
    }

    setErrors({});
    setLoading(true);

    const { error } = await authClient.resetPassword({ newPassword: parsed.data.password, token });

    setLoading(false);

    if (error !== null && error !== undefined) {
      // Одноразовость и срок жизни ссылки - это не сбой, а нормальное
      // состояние: объясняем и даём выход.
      if (error.status === 400 || error.status === 401) {
        setExpired(true);
        return;
      }

      setFormError(
        reasonFrom(error, 'Не удалось сменить пароль. Проверьте соединение и попробуйте ещё раз.'),
      );
      return;
    }

    navigate('/login', { replace: true, state: { passwordChanged: true } });
  }

  if (expired) {
    return (
      <AuthLayout
        title="Ссылка больше не работает"
        footer={
          <RouterLink className={LINK_CLASS} to="/login">
            Вернуться ко входу
          </RouterLink>
        }
      >
        <Notice tone="warning" title="Такое бывает по двум причинам">
          Ссылка действует ограниченное время и срабатывает один раз. Если вы уже меняли пароль по
          ней или письмо пролежало слишком долго — запросите новую.
        </Notice>

        <div className="mt-2 flex flex-col gap-3">
          <Button
            variant="primary"
            block
            onClick={() => {
              void navigate('/forgot-password');
            }}
          >
            Запросить новую ссылку
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title="Новый пароль" subtitle="После смены пароля все сессии останутся активными">
      <form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
        {formError !== null ? (
          <div>
            <Notice tone="error" title="Не получилось" live>
              {formError}
            </Notice>
          </div>
        ) : null}

        <Field label="Новый пароль" hint="Не короче 8 символов" error={errors['password']} required>
          {(props) => (
            <Input
              {...props}
              type="password"
              value={password}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
              autoComplete="new-password"
              autoFocus
            />
          )}
        </Field>

        <Field label="Повторите пароль" error={errors['passwordRepeat']} required>
          {(props) => (
            <Input
              {...props}
              type="password"
              value={passwordRepeat}
              onChange={(event) => {
                setPasswordRepeat(event.target.value);
              }}
              autoComplete="new-password"
            />
          )}
        </Field>

        <div className="mt-2 flex flex-col gap-3">
          <Button type="submit" variant="primary" block loading={loading}>
            Сменить пароль
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
